import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { formatMessageTime, parseDate } from '../shared/dateUtils';
import { S3Image, getAvatarStyle, isNewStyleAvatar } from '../../../../lib/s3';
import Button from '../../../../../../ui/canon/Button';
import { MagnifyingGlassIcon, XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useSuccessionTheme } from '../../SuccessionThemeProvider';

/**
 * MessageSearch - In-conversation message search component
 * 
 * Features real-time search, result highlighting, and navigation
 */
const MessageSearch = ({ 
  messages = [], 
  currentUser, 
  conversation,
  onResultSelect, 
  isOpen, 
  onClose 
}) => {
  const intl = useIntl();
  const { company: companySettings } = useSuccessionTheme();
  // Helper to detect Paddy messages (messages without sender in Paddy conversations)
  const isPaddyMessage = (message) => {
    return !message.sender?.id && 
      ['paddy', 'support_request'].includes(conversation?.conversationType);
  };

  // Get sender info with Paddy handling
  const getSenderInfo = (message) => {
    if (isPaddyMessage(message)) {
      return {
        name: 'Paddy',
        profile_picture: 'paddy.png'
      };
    }
    return {
      name: message.sender?.allowed_name || message.sender?.name || 'Unknown',
      profile_picture: message.sender?.profile_picture
    };
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef(null);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Perform search when query changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setCurrentResultIndex(-1);
      return;
    }

    setIsSearching(true);
    
    // Debounce search
    const debounceTimer = setTimeout(() => {
      performSearch(searchQuery);
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, messages]);

  const performSearch = (query) => {
    const lowercaseQuery = query.toLowerCase().trim();
    if (!lowercaseQuery) return;

    const results = messages
      .map((message, index) => ({ ...message, originalIndex: index }))
      .filter(message => {
        const senderInfo = getSenderInfo(message);
        return message.body.toLowerCase().includes(lowercaseQuery) ||
          senderInfo.name.toLowerCase().includes(lowercaseQuery);
      })
      .map(message => ({
        ...message,
        highlightedBody: highlightSearchTerm(message.body, query)
      }))
      .reverse(); // Show newest results first

    setSearchResults(results);
    setCurrentResultIndex(results.length > 0 ? 0 : -1);
  };

  const highlightSearchTerm = (text, term) => {
    if (!term.trim()) return text;
    
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  };

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setCurrentResultIndex(prev => {
          const newIndex = prev < searchResults.length - 1 ? prev + 1 : 0;
          // Scroll to the selected result
          setTimeout(() => {
            const element = document.querySelector(`[data-result-index="${newIndex}"]`);
            element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }, 0);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setCurrentResultIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : searchResults.length - 1;
          // Scroll to the selected result
          setTimeout(() => {
            const element = document.querySelector(`[data-result-index="${newIndex}"]`);
            element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }, 0);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (currentResultIndex >= 0 && searchResults[currentResultIndex]) {
          selectResult(searchResults[currentResultIndex]);
        }
        break;
    }
  };

  const selectResult = (result) => {
    onResultSelect(result.originalIndex);
    onClose();
  };

  // Using Safari-compatible formatMessageTime from dateUtils - customized for search
  const formatSearchTime = (timestamp) => {
    const date = parseDate(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (isNaN(diffInHours) || isNaN(date.getTime())) {
      console.warn('formatSearchTime: Invalid date:', timestamp);
      return 'Invalid date';
    }
    
    try {
      if (diffInHours < 24) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      // Try new Intl.DateTimeFormat for better Safari compatibility
      const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
      return formatter.format(date);
    } catch (error) {
      try {
        // Fallback to toLocaleDateString
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      } catch (error2) {
        console.warn('Error formatting search date:', timestamp, error, error2);
        // Final fallback - manual formatting
        const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getMonth()];
        return `${month} ${date.getDate()}`;
      }
    }
  };

  if (!isOpen) return null;

  // Use Portal to render at document body level for proper overlay coverage
  return ReactDOM.createPortal(
    <>
      {/* Dark overlay backdrop - covers entire page with blur */}
      <div 
        className="fixed inset-0"
        style={{ 
          zIndex: 9998,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)'
        }}
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal container - centered on page, click outside closes */}
      <div 
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{ zIndex: 9999 }}
        onClick={onClose}
      >
        <div 
          className="bg-succession-bg-primary rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col border border-succession-border"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
      {/* Search Header */}
      <div className="px-6 py-5 border-b border-succession-border bg-succession-bg-primary">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-succession-primary flex items-center justify-center flex-shrink-0">
            <MagnifyingGlassIcon className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-xl font-bold text-succession-text-primary m-0 flex-1 tracking-tight">
            {intl.formatMessage({ id: 'succession.messaging.message_search.title' })}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="w-9 h-9 rounded-lg !p-0 text-succession-text-secondary hover:!bg-succession-error/10 hover:!text-succession-error transition-all"
            aria-label={intl.formatMessage({ id: 'succession.messaging.message_search.close_aria' })}
          >
            <XMarkIcon className="w-5 h-5" />
          </Button>
        </div>

        {/* Search Input - with icon prefix separator like conversation search */}
        <div className="flex items-center bg-succession-bg-secondary rounded-lg border border-succession-border overflow-hidden">
          {/* Icon prefix with separator */}
          <div className="flex items-center justify-center px-4 border-r border-succession-border">
            <MagnifyingGlassIcon className="w-5 h-5 text-succession-text-muted" />
          </div>
          {/* Input field */}
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={intl.formatMessage({ id: 'succession.messaging.message_search.placeholder' })}
            className="flex-1 px-4 py-3 bg-transparent text-succession-text-primary placeholder-succession-text-muted outline-none border-none text-base"
          />
        </div>

        {/* Results Summary */}
        {searchQuery && (
          <div className={`mt-3 px-3 py-2.5 rounded-lg flex items-center justify-between text-sm ${
            searchResults.length > 0 
              ? 'bg-succession-primary/10 border border-succession-primary/20' 
              : 'bg-succession-warning/10 border border-succession-warning/20'
          }`}>
            <div className={`flex items-center gap-2 ${
              searchResults.length > 0 ? 'text-succession-primary' : 'text-succession-warning'
            }`}>
              {isSearching ? (
                <>
                  <div className="w-4 h-4 border-2 border-transparent border-t-current rounded-full animate-spin" />
                  <span className="font-medium">{intl.formatMessage({ id: 'succession.messaging.message_search.searching' })}</span>
                </>
              ) : searchResults.length === 0 ? (
                <>
                  <ExclamationTriangleIcon className="w-4 h-4" />
                  <span className="font-medium">{intl.formatMessage({ id: 'succession.messaging.message_search.no_results' })}</span>
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-4 h-4" />
                  <span className="font-semibold">
                    {intl.formatMessage({ id: 'succession.messaging.message_search.results_found' }, { count: searchResults.length, plural: searchResults.length !== 1 ? 's' : '' })}
                  </span>
                </>
              )}
            </div>
            {searchResults.length > 0 && (
              <div className="flex items-center gap-1.5 text-succession-text-muted text-xs">
                <kbd className="px-1.5 py-0.5 bg-succession-bg-primary border border-succession-border rounded text-[11px] font-mono">↑↓</kbd>
                <span>{intl.formatMessage({ id: 'succession.messaging.message_search.navigate_hint' })}</span>
                <kbd className="px-1.5 py-0.5 bg-succession-bg-primary border border-succession-border rounded text-[11px] font-mono">↵</kbd>
                <span>{intl.formatMessage({ id: 'succession.messaging.message_search.select_hint' })}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Search Results */}
      <div 
        className="message-search-results flex-1 overflow-y-auto p-3"
        style={{ maxHeight: 'calc(80vh - 280px)' }}
      >
        {searchResults.map((result, index) => {
          const senderInfo = getSenderInfo(result);
          const senderName = senderInfo.name;
          const senderProfilePicture = senderInfo.profile_picture;
          const isSelected = index === currentResultIndex;

          return (
            <div
              key={`${result.id}-${index}`}
              data-result-index={index}
              onClick={() => selectResult(result)}
              className={`mx-2 mb-2 p-3 rounded-xl cursor-pointer transition-all duration-150 ${
                isSelected 
                  ? 'bg-succession-primary/10 ring-2 ring-succession-primary shadow-lg' 
                  : 'bg-succession-bg-primary border border-succession-border hover:bg-succession-bg-secondary hover:shadow-md'
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Avatar */}
                <div className="w-9 h-9 rounded-full flex-shrink-0 overflow-hidden bg-succession-bg-tertiary">
                  {senderProfilePicture ? (
                    <img
                      src={getAvatarStyle(senderProfilePicture, companySettings).url}
                      alt={senderName}
                      className="w-full h-full object-cover"
                      style={isNewStyleAvatar(senderProfilePicture) ? { objectFit: 'contain', objectPosition: 'bottom center', transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`${senderProfilePicture ? 'hidden' : 'flex'} w-full h-full items-center justify-center text-white text-sm font-medium bg-succession-primary`}
                  >
                    {senderName.charAt(0).toUpperCase()}
                  </div>
                </div>

                {/* Message content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-semibold text-succession-text-primary">
                      {senderName}
                    </span>
                    <span className="text-xs text-succession-text-muted">
                      {formatSearchTime(result.createdAt)}
                    </span>
                  </div>
                  <div 
                    className="text-sm text-succession-text-secondary leading-relaxed break-words line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: result.highlightedBody }}
                  />
                </div>
              </div>
            </div>
          );
        })}

        {/* Empty state */}
        {searchQuery && !isSearching && searchResults.length === 0 && (
          <div className="py-12 px-6 text-center">
            <div className="w-14 h-14 bg-succession-bg-secondary rounded-2xl flex items-center justify-center mx-auto mb-4">
              <MagnifyingGlassIcon className="w-6 h-6 text-succession-text-muted" />
            </div>
            <div className="mb-1 text-base font-semibold text-succession-text-primary">
              {intl.formatMessage({ id: 'succession.messaging.message_search.no_results' })}
            </div>
            <p className="m-0 text-sm text-succession-text-muted">
              {intl.formatMessage({ id: 'succession.messaging.message_search.no_match' }, { query: searchQuery })}
            </p>
          </div>
        )}
      </div>

      {/* Search Tips - shown when no query */}
      {!searchQuery && (
        <div className="px-6 py-5 bg-succession-bg-secondary border-t border-succession-border">
          <div className="flex items-center gap-2 mb-3">
            <InformationCircleIcon className="w-4 h-4 text-succession-primary" />
            <span className="text-sm font-semibold text-succession-text-primary">
              {intl.formatMessage({ id: 'succession.messaging.message_search.tips_title' })}
            </span>
          </div>
          <ul className="space-y-1.5 text-sm text-succession-text-muted m-0 p-0 list-none">
            <li className="flex items-start gap-2">
              <span className="text-succession-primary mt-1">•</span>
              <span>{intl.formatMessage({ id: 'succession.messaging.message_search.tip_content' })}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-succession-primary mt-1">•</span>
              <span>{intl.formatMessage({ id: 'succession.messaging.message_search.tip_navigate' })}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-succession-primary mt-1">•</span>
              <span>{intl.formatMessage({ id: 'succession.messaging.message_search.tip_escape' })}</span>
            </li>
          </ul>
        </div>
      )}

      {/* Styles for highlighted search terms */}
      <style>
        {`
          .message-search-results mark {
            background: rgb(var(--color-succession-warning) / 0.2);
            color: var(--succession-warning);
            padding: 1px 3px;
            border-radius: 3px;
            font-weight: 600;
          }
          
          .message-search-results::-webkit-scrollbar {
            width: 6px;
          }
          
          .message-search-results::-webkit-scrollbar-track {
            background: transparent;
          }
          
          .message-search-results::-webkit-scrollbar-thumb {
            background: var(--succession-border);
            border-radius: 3px;
          }
          
          .message-search-results::-webkit-scrollbar-thumb:hover {
            background: var(--succession-text-muted);
          }
        `}
      </style>
        </div>
      </div>
    </>,
    document.body
  );
};

MessageSearch.propTypes = {
  messages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    body: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    sender: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      allowed_name: PropTypes.string,
      profile_picture: PropTypes.string
    })
  })),
  currentUser: PropTypes.object.isRequired,
  conversation: PropTypes.shape({
    conversationType: PropTypes.string
  }),
  onResultSelect: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default MessageSearch; 